<template>
	<Accordion
		:title="title"
		:showClear="showClear"
		@clear="clearFilter"
	>
		<template #content>
			<ClientOnly>
				<component
					v-if="filterComponent"
					:is="filterComponent"
				/>
			</ClientOnly>
		</template>
		<template #footer>
			<a class="Selection">{{ selection }}</a>
		</template>
	</Accordion>
</template>

<script setup lang="ts">
import Accordion from '@components/Accordion/Accordion.vue';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { useSearchStore } from '@app/stores/useSearchStore';
import { useFilterSelectionText } from '@composables/useFilterSelectionText';
import { computed, type Component, PropType } from 'vue';
import { FilterProcessingService } from '@app/routing/FilterProcessingService';
import { PortalType } from '@studyportals/domain-client';

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	filterTypes: {
		type: Array as PropType<FilterType[]>,
		required: true,
	},
	filterComponent: {
		type: Object as PropType<Component>,
		default: null,
	},
});

const searchStore = useSearchStore();
const filtersProcessingService = FilterProcessingService.getInstance();
const selection = useFilterSelectionText(props.filterTypes);

const showClear = computed(() => {
	const hasNonImplicit = props.filterTypes.some((filterType) => {
		const filter = searchStore.filtersSelection.find((f) => f.type === filterType);
		if (!filter) return false;

		const mapper = filtersProcessingService.mappers.find((m) => m.filterType === filterType);
		if (!mapper) return filter.values.length > 0;

		return filter.values.some(
			(v) => !mapper.isImplicitForPortalType(v.id, searchStore.portalType as PortalType)
		);
	});
	return hasNonImplicit;
});

const clearFilter = (): void => {
	searchStore.removeFilterByType(props.filterTypes);
};
</script>

<style scoped lang="scss">
	.Selection {
		display: -webkit-box;
		-webkit-line-clamp: 2;
		line-clamp: 2;
		-webkit-box-orient: vertical;
		overflow: hidden;
		text-overflow: ellipsis;
		word-break: break-word;
		color: $Grey-700;
		user-select: none;

		@include TextStyle(BodyMediumRegular);
	}
</style>
